import { MAX_TILE_ID } from "../document/packed-tile.ts";
import type { TileRef } from "../document/types.ts";
import { asArray, asBoolean, asInteger, asRecord, asString } from "../document/validate.ts";
import { packOf } from "../editor/packs.ts";
import {
  TILE_PROPS_SCHEMA_VERSION,
  TILE_PROP_CELLS_MAX,
  TILE_PROP_SIDE_MAX,
  TILE_PROP_STACK_MAX,
  TILE_PROP_TEXT_MAX,
  isTilePropsTileset,
  type TileProps,
  type TilePropCell,
  type TilePropEntry,
  type TilePropSheets,
} from "./types.ts";

/**
 * The tile props file (ADR-102 decisions 2 to 4): plain JSON in canonical form, keys in a fixed order and entries
 * by id, so a document nobody changed is the same bytes, and a reader that refuses every bound of decision 3 with
 * the path of the first fault.
 */

const ROOT = "tileprops";
const DOCUMENT_KEYS: readonly string[] = ["schemaVersion", "pack", "next", "props"];
const ENTRY_KEYS: readonly string[] = ["id", "rev", "name", "group", "cols", "rows", "base", "retired", "cells"];
const CELL_KEYS: readonly string[] = ["x", "y", "tile"];
const TILE_KEYS: readonly string[] = ["tileset", "tile", "flipX", "flipY"];

/**
 * Writes a document in its canonical form: two-space indented, every cell on one line, entries by id, cells in
 * draw order, a flag written only when true, ending in a newline.
 *
 * @param document The document.
 * @returns The file's text.
 */
export function writeTileProps(document: TileProps): string {
  const entries = [...document.props].sort((a, b) => a.id - b.id).map(entryText);
  const props = entries.length === 0 ? "[]" : `[\n${entries.join(",\n")}\n  ]`;
  return `{\n  "schemaVersion": ${TILE_PROPS_SCHEMA_VERSION},\n  "pack": ${JSON.stringify(document.pack)},\n  "next": ${document.next},\n  "props": ${props}\n}\n`;
}

/**
 * One entry as canonical text, indented for its place in the document.
 *
 * @param entry The entry.
 * @returns The text, without a trailing newline.
 */
function entryText(entry: TilePropEntry): string {
  const fields = [
    `"id": ${entry.id}`,
    `"rev": ${entry.rev}`,
    `"name": ${JSON.stringify(entry.name)}`,
    `"group": ${JSON.stringify(entry.group)}`,
    `"cols": ${entry.cols}`,
    `"rows": ${entry.rows}`,
    ...(entry.base === undefined ? [] : [`"base": ${entry.base}`]),
    ...(entry.retired === true ? ['"retired": true'] : []),
  ];
  const cells = entry.cells.map((cell) => `        ${cellText(cell)}`).join(",\n");
  return `    {\n${fields.map((field) => `      ${field},\n`).join("")}      "cells": [\n${cells}\n      ]\n    }`;
}

/**
 * One cell as a line of canonical text.
 *
 * @param cell The cell.
 * @returns The text.
 */
function cellText(cell: TilePropCell): string {
  const tile = cell.tile;
  const flags = `${tile.flipX === true ? ', "flipX": true' : ""}${tile.flipY === true ? ', "flipY": true' : ""}`;
  return `{ "x": ${cell.x}, "y": ${cell.y}, "tile": { "tileset": ${JSON.stringify(tile.tileset)}, "tile": ${tile.tile}${flags} } }`;
}

/**
 * Reads a document, checking every bound of ADR-102 decision 3.
 *
 * @param text The file's text.
 * @param sheets The pack's tilesets where the host has them. With it, an entry may not draw from an image
 *   collection or from tilesets of two tile sizes, and its `base` may not pass its frame's height; a tileset it
 *   does not know is left alone.
 * @returns The document, its cells normalised.
 * @throws Error whose message starts with the path of the first fault: not JSON, another schema, a bad pack or
 *   `next`, an id out of order, a name or group out of bounds or repeated, a side past 30, no cells or more than
 *   2,048, a cell outside the frame or stacked past 4 or drawn from another pack, `tile-props` itself or an
 *   image collection, or a bad `base`.
 */
export function readTileProps(text: string, sheets?: TilePropSheets): TileProps {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error(`${ROOT} must be JSON`);
  }
  const record = asRecord(parsed, ROOT);
  onlyKeys(record, DOCUMENT_KEYS, ROOT);
  const version = asInteger(record["schemaVersion"], `${ROOT}.schemaVersion`);
  if (version !== TILE_PROPS_SCHEMA_VERSION) {
    throw new Error(`${ROOT}.schemaVersion: documents of schema ${version} are not read by this build, which reads schema ${TILE_PROPS_SCHEMA_VERSION}`);
  }
  const pack = asString(record["pack"], `${ROOT}.pack`);
  if (pack === "" || pack.includes(":") || pack.includes("/")) {
    throw new Error(`${ROOT}.pack must be a non-empty pack id with no ":" or "/"`);
  }
  const next = asInteger(record["next"], `${ROOT}.next`);
  const props: TilePropEntry[] = [];
  const names = new Map<string, number>();
  asArray(record["props"], `${ROOT}.props`).forEach((value, i) => {
    const entry = readEntry(value, pack, props[i - 1]?.id ?? -1, sheets, `${ROOT}.props[${i}]`);
    const key = entry.name.toLowerCase();
    if (names.has(key)) {
      throw new Error(`${ROOT}.props[${i}].name ${JSON.stringify(entry.name)} is already the name of entry ${names.get(key)}, names being unique in a pack ignoring case`);
    }
    names.set(key, entry.id);
    props.push(entry);
  });
  if (next < 0 || next > MAX_TILE_ID + 1 || props.some((entry) => entry.id >= next)) {
    throw new Error(`${ROOT}.next must be above every id and at most ${MAX_TILE_ID + 1}, not ${next}`);
  }
  return { schemaVersion: TILE_PROPS_SCHEMA_VERSION, pack, next, props };
}

/**
 * Refuses a key a record should not hold, so nothing a later build writes is dropped unseen by this one.
 *
 * @param record The record.
 * @param allowed The keys it may hold.
 * @param path Path of the record.
 * @throws Error naming the path of the first other key.
 */
function onlyKeys(record: Record<string, unknown>, allowed: readonly string[], path: string): void {
  for (const key of Object.keys(record)) {
    if (!allowed.includes(key)) {
      throw new Error(`${path}.${key} is not a field of this schema`);
    }
  }
}

/**
 * Asserts an integer in a range.
 *
 * @param value Candidate value.
 * @param low Inclusive lower bound.
 * @param high Inclusive upper bound.
 * @param path Path for the message.
 * @returns The integer.
 * @throws Error naming the path when the value is not an integer in `[low, high]`.
 */
function integerBetween(value: unknown, low: number, high: number, path: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < low || value > high) {
    throw new Error(`${path} must be an integer from ${low} to ${high}`);
  }
  return value;
}

/**
 * Reads one entry.
 *
 * @param value Candidate value.
 * @param pack The document's pack.
 * @param previous The previous entry's id, or -1.
 * @param sheets The host's tilesets, if any.
 * @param path Path for messages.
 * @returns The entry.
 * @throws Error naming the path of the first fault.
 */
function readEntry(value: unknown, pack: string, previous: number, sheets: TilePropSheets | undefined, path: string): TilePropEntry {
  const record = asRecord(value, path);
  onlyKeys(record, ENTRY_KEYS, path);
  const id = integerBetween(record["id"], 0, MAX_TILE_ID, `${path}.id`);
  if (id <= previous) {
    throw new Error(`${path}.id ${id} must be above the id before it: entries are by ascending id, each once`);
  }
  const rev = integerBetween(record["rev"], 1, Number.MAX_SAFE_INTEGER, `${path}.rev`);
  const name = asString(record["name"], `${path}.name`);
  if (name.length < 1 || name.length > TILE_PROP_TEXT_MAX) {
    throw new Error(`${path}.name must be 1 to ${TILE_PROP_TEXT_MAX} characters`);
  }
  const group = asString(record["group"], `${path}.group`);
  if (group.length < 1 || group.length > TILE_PROP_TEXT_MAX || group.includes("/")) {
    throw new Error(`${path}.group must be 1 to ${TILE_PROP_TEXT_MAX} characters with no "/"`);
  }
  const cols = integerBetween(record["cols"], 1, TILE_PROP_SIDE_MAX, `${path}.cols`);
  const rows = integerBetween(record["rows"], 1, TILE_PROP_SIDE_MAX, `${path}.rows`);
  const base = record["base"] === undefined ? undefined : integerBetween(record["base"], 1, Number.MAX_SAFE_INTEGER, `${path}.base`);
  const retired = record["retired"] === undefined ? false : asBoolean(record["retired"], `${path}.retired`);
  const list = asArray(record["cells"], `${path}.cells`);
  if (list.length === 0) {
    throw new Error(`${path}.cells must hold at least one cell`);
  }
  if (list.length > TILE_PROP_CELLS_MAX) {
    throw new Error(`${path}.cells holds at most ${TILE_PROP_CELLS_MAX} cells, not ${list.length}`);
  }
  const stacks = new Map<number, number>();
  let tileSize: number | undefined;
  const cells = list.map((cell, i) => {
    const read = readCell(cell, pack, cols, rows, `${path}.cells[${i}]`);
    const place = read.y * TILE_PROP_SIDE_MAX + read.x;
    const height = (stacks.get(place) ?? 0) + 1;
    if (height > TILE_PROP_STACK_MAX) {
      throw new Error(`${path}.cells[${i}] stacks tile ${height} at (${read.x}, ${read.y}); at most ${TILE_PROP_STACK_MAX} share a place`);
    }
    stacks.set(place, height);
    const sheet = sheets?.(read.tile.tileset);
    if (sheet !== undefined) {
      if (!sheet.grid) {
        throw new Error(`${path}.cells[${i}].tile.tileset ${JSON.stringify(read.tile.tileset)} is an image collection, and an entry draws from grid tilesets only`);
      }
      if (tileSize !== undefined && sheet.tileSize !== tileSize) {
        throw new Error(`${path}.cells[${i}].tile.tileset ${JSON.stringify(read.tile.tileset)} has ${sheet.tileSize} px tiles, but the entry's other tilesets have ${tileSize} px`);
      }
      tileSize = sheet.tileSize;
    }
    return read;
  });
  if (base !== undefined && tileSize !== undefined && base > rows * tileSize) {
    throw new Error(`${path}.base ${base} is past the frame's height of ${rows * tileSize} px`);
  }
  return { id, rev, name, group, cols, rows, ...(base === undefined ? {} : { base }), ...(retired ? { retired: true as const } : {}), cells };
}

/**
 * Reads one cell.
 *
 * @param value Candidate value.
 * @param pack The document's pack.
 * @param cols The entry's columns.
 * @param rows The entry's rows.
 * @param path Path for messages.
 * @returns The cell.
 * @throws Error naming the path of the first fault.
 */
function readCell(value: unknown, pack: string, cols: number, rows: number, path: string): TilePropCell {
  const record = asRecord(value, path);
  onlyKeys(record, CELL_KEYS, path);
  const x = integerBetween(record["x"], 0, cols - 1, `${path}.x`);
  const y = integerBetween(record["y"], 0, rows - 1, `${path}.y`);
  const tile = asRecord(record["tile"], `${path}.tile`);
  onlyKeys(tile, TILE_KEYS, `${path}.tile`);
  const tileset = asString(tile["tileset"], `${path}.tile.tileset`);
  if (packOf(tileset) !== pack || tileset.length === pack.length + 1) {
    throw new Error(`${path}.tile.tileset ${JSON.stringify(tileset)} must be a tileset of the pack ${JSON.stringify(pack)}`);
  }
  if (isTilePropsTileset(tileset)) {
    throw new Error(`${path}.tile.tileset ${JSON.stringify(tileset)} is the tile props tileset, which an entry may not draw from`);
  }
  const index = integerBetween(tile["tile"], 0, MAX_TILE_ID, `${path}.tile.tile`);
  const flipX = tile["flipX"] === undefined ? false : asBoolean(tile["flipX"], `${path}.tile.flipX`);
  const flipY = tile["flipY"] === undefined ? false : asBoolean(tile["flipY"], `${path}.tile.flipY`);
  const ref: TileRef = { tileset, tile: index, ...(flipX ? { flipX: true } : {}), ...(flipY ? { flipY: true } : {}) };
  return { x, y, tile: ref };
}
